import { App } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { showFileAction } from "../../../utils/documents/show-file-action.ts"
import { downloadFile } from "../../../utils/download-file.ts"
import {
  changeAttachmentStatus,
  deleteAttachment,
  emptyAttachmentsTrash,
  moveAttachment,
  permanentlyDeleteAttachment,
  renameAttachment,
  restoreAttachmentFromTrash
} from "../../Album/api/attachment.services.ts"
import { addAlbumAttachments } from "../../Album/redux/album.thunks.ts"
import {
  approveDocumentReview,
  rejectDocumentReview,
  requestChangesOnReview,
  requestDocumentReview
} from "../../Project/api/project-attachment-workflow.services.ts"
import {
  changeDocumentStatus,
  deleteProjectDocument,
  emptyProjectDocumentsTrash,
  moveProjectDocument,
  permanentlyDeleteProjectDocument,
  restoreProjectDocumentFromTrash,
  restoreProjectDocumentVersion,
  updateProjectDocumentClassification
} from "../../Project/api/project-documents.services.ts"
import type { DocumentReview } from "../../Project/types/project-attachment-workflow.types.ts"
import type { RelatedRecordType } from "../../Project/types/project-documents.types.ts"
import {
  createFolder,
  deleteFolder,
  getFolders,
  updateFolder
} from "../api/folder.services.ts"
import { canRequestReview, canViewReview } from "../helpers/document-review-status.ts"
import { readServerMessage } from "../helpers/server-message.ts"
import { useDocumentTypeOptions } from "./useDocumentTypeOptions.ts"
import { useStatusOptions } from "./useStatusOptions.ts"
import { type FileItem, type FileRights } from "../types/file-item.types.ts"
import { type FolderListItem } from "../types/folder.types.ts"

interface FolderRights {
  createAllowed: boolean
  deleteAllowed: boolean
}

interface UseFileActionsArgs {
  projectId: string
  currentFolderId: string | null
  fileItems: FileItem[]
  rights: FileRights & FolderRights
  reload: () => Promise<void>
}

type BulkOutcome = { ok: boolean }

export function useFileActions({
  projectId,
  currentFolderId,
  fileItems,
  rights,
  reload
}: UseFileActionsArgs) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false)
  const [renamingFolder, setRenamingFolder] = useState<FolderListItem | null>(null)
  const [movingFiles, setMovingFiles] = useState<FileItem[] | null>(null)
  const [movingFolder, setMovingFolder] = useState<FolderListItem | null>(null)
  const [allFoldersForMove, setAllFoldersForMove] = useState<FolderListItem[]>([])
  const [addingToAlbumFiles, setAddingToAlbumFiles] = useState<FileItem[] | null>(
    null
  )
  const [renamingFile, setRenamingFile] = useState<FileItem | null>(null)
  const [assigningDocumentTypeFile, setAssigningDocumentTypeFile] = useState<FileItem | null>(
    null
  )
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [linkingFile, setLinkingFile] = useState<FileItem | null>(null)
  const [uploadingNewVersionFile, setUploadingNewVersionFile] = useState<FileItem | null>(
    null
  )
  const [versionHistoryFile, setVersionHistoryFile] = useState<FileItem | null>(null)
  const [requestReviewFile, setRequestReviewFile] = useState<FileItem | null>(null)
  const [reviewingFile, setReviewingFile] = useState<FileItem | null>(null)
  const [rejectingReview, setRejectingReview] = useState<DocumentReview | null>(null)
  const [requestingChangesReview, setRequestingChangesReview] = useState<DocumentReview | null>(
    null
  )
  const [commentsFile, setCommentsFile] = useState<FileItem | null>(null)
  const [activityFile, setActivityFile] = useState<FileItem | null>(null)
  const [sharingFile, setSharingFile] = useState<FileItem | null>(null)
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0)
  const { options: documentTypeOptions, loadFailed: documentTypesFailed } =
    useDocumentTypeOptions()
  const { options: statusOptions, loadFailed: statusesFailed } = useStatusOptions()

  // A workflow action (status change, review request/approve/reject, a
  // comment) reloads `fileItems` but any of these open-panel snapshots keeps
  // showing the pre-action row until closed and reopened, since none of them
  // are derived from `fileItems` themselves. Resync each by key so a panel
  // left open through a reload always reflects the freshest row.
  useEffect(() => {
    const resync = (file: FileItem | null) => {
      if (!file) return file
      const fresh = fileItems.find((item) => item.key === file.key)
      return fresh && fresh !== file ? fresh : file
    }
    setLinkingFile(resync)
    setAssigningDocumentTypeFile(resync)
    setVersionHistoryFile(resync)
    setRequestReviewFile(resync)
    setReviewingFile(resync)
    setCommentsFile(resync)
    setActivityFile(resync)
    setSharingFile(resync)
  }, [fileItems])

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), [])

  const toggleSelection = useCallback((file: FileItem) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }, [])

  const selectedFiles = useMemo(
    () => fileItems.filter((file) => selectedKeys.has(file.key)),
    [fileItems, selectedKeys]
  )

  const canManageFolder = useCallback(
    (folder: FolderListItem) =>
      showFileAction({
        file: folder,
        allowed: rights.editAllowed,
        foreignAllowed: rights.editForeignAllowed,
        currentUserId: rights.currentUserId
      }),
    [rights]
  )

  const reportBulk = useCallback(
    (
      outcomes: BulkOutcome[],
      keys: { succeeded: string; partial: string; failed: string }
    ) => {
      const done = outcomes.filter((outcome) => outcome.ok).length
      const total = outcomes.length
      if (done === total) message.success(t(keys.succeeded, { count: total }))
      else if (done === 0) message.error(t(keys.failed, { count: total }))
      else message.warning(t(keys.partial, { done, total }))
    },
    [message, t]
  )

  const createFolderHere = useCallback(
    async (name: string) => {
      const response = await createFolder({
        project: projectId,
        parent: currentFolderId,
        name
      })
      if (!response.ok) {
        const reason =
          readServerMessage(response.data) ?? t("files.newFolder.failed")
        message.error(reason)
        // Rejecting is what keeps the modal open with the typed name intact;
        // returning quietly would close it and throw the name away.
        throw new Error(reason)
      }
      message.success(t("files.newFolder.succeeded"))
      await reload()
    },
    [projectId, currentFolderId, message, t, reload]
  )

  const renameFolder = useCallback(
    async (name: string) => {
      if (!renamingFolder) return
      const response = await updateFolder(renamingFolder.id, { name })
      if (!response.ok) {
        const reason =
          readServerMessage(response.data) ?? t("files.rename.folderFailed")
        message.error(reason)
        throw new Error(reason)
      }
      await reload()
    },
    [renamingFolder, message, t, reload]
  )

  const removeFolder = useCallback(
    async (folder: FolderListItem) => {
      const response = await deleteFolder(folder.id)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ?? t("files.folder.deleteFailed")
        )
        return
      }
      message.success(t("files.folder.deleteSucceeded"))
      await reload()
    },
    [message, t, reload]
  )

  const moveFolderTo = useCallback(
    async (folderId: string, parentId: string | null) => {
      const response = await updateFolder(folderId, { parent: parentId })
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ?? t("files.moveFolder.failed")
        )
        return
      }
      message.success(t("files.moveFolder.succeeded"))
      await reload()
    },
    [message, t, reload]
  )

  const openMoveFolderModal = useCallback(
    async (folder: FolderListItem) => {
      const response = await getFolders({ project: projectId })
      if (response.ok && response.data) {
        setAllFoldersForMove(response.data.results || [])
      }
      setMovingFolder(folder)
    },
    [projectId]
  )

  const deleteOne = (file: FileItem) =>
    file.source === "album-attachment"
      ? deleteAttachment(file.id)
      : deleteProjectDocument(file.id)

  const moveOne = (file: FileItem, folderId: string | null) =>
    file.source === "album-attachment"
      ? moveAttachment(file.id, { folder: folderId })
      : moveProjectDocument(file.id, { folder: folderId })

  const removeFile = useCallback(
    async (file: FileItem) => {
      const response = await deleteOne(file)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ??
            t("files.delete.failed", { name: file.name })
        )
        return
      }
      message.success(t("files.delete.succeeded", { name: file.name }))
      await reload()
    },
    [message, t, reload]
  )

  const restoreFile = useCallback(
    async (file: FileItem) => {
      const response =
        file.source === "album-attachment"
          ? await restoreAttachmentFromTrash(file.id)
          : await restoreProjectDocumentFromTrash(file.id)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ??
            t("files.restore.failed", { name: file.name })
        )
        return false
      }
      // A restored document with `folder: null` either never had one or its
      // folder was deleted in the meantime — either way it lands in the
      // project root, and the only thing worth telling the user is why.
      const restored = response.data?.details?.data as { folder?: string | null } | undefined
      if (file.folderId && restored?.folder === null) {
        message.info(t("files.restore.landedInRoot", { name: file.name }))
      } else {
        message.success(t("files.restore.succeeded", { name: file.name }))
      }
      await reload()
      return true
    },
    [message, t, reload]
  )

  const permanentlyDeleteFile = useCallback(
    async (file: FileItem) => {
      const response =
        file.source === "album-attachment"
          ? await permanentlyDeleteAttachment(file.id)
          : await permanentlyDeleteProjectDocument(file.id)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ??
            t("files.permanentDelete.failed", { name: file.name })
        )
        return false
      }
      message.success(t("files.permanentDelete.succeeded", { name: file.name }))
      await reload()
      return true
    },
    [message, t, reload]
  )

  const emptyTrash = useCallback(async () => {
    const [documentsResponse, attachmentsResponse] = await Promise.all([
      emptyProjectDocumentsTrash(),
      emptyAttachmentsTrash()
    ])
    if (!documentsResponse.ok || !attachmentsResponse.ok) {
      message.error(t("files.emptyTrash.failed"))
      return false
    }
    message.success(t("files.emptyTrash.succeeded"))
    await reload()
    return true
  }, [message, t, reload])

  const moveFile = useCallback(
    async (file: FileItem, folderId: string | null) => {
      const response = await moveOne(file, folderId)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ??
            t("files.move.failed", { name: file.name })
        )
        return
      }
      message.success(t("files.move.succeeded", { name: file.name }))
      await reload()
    },
    [message, t, reload]
  )

  const moveFileById = useCallback(
    (fileKey: string, folderId: string | null) => {
      const file = fileItems.find((item) => item.key === fileKey)
      if (file?.canMove) void moveFile(file, folderId)
    },
    [fileItems, moveFile]
  )

  const renameFile = useCallback(
    async (file: FileItem, nextName: string) => {
      if (file.source !== "album-attachment") return
      const response = await renameAttachment(file.id, {
        original_filename: nextName
      })
      if (!response.ok) {
        const reason = readServerMessage(response.data) ?? t("files.rename.failed")
        message.error(reason)
        throw new Error(reason)
      }
      await reload()
    },
    [message, t, reload]
  )

  const bulkMove = useCallback(
    async (files: FileItem[], folderId: string | null) => {
      const outcomes = await Promise.all(files.map((file) => moveOne(file, folderId)))
      reportBulk(outcomes, {
        succeeded: "files.bulk.moveSucceeded",
        partial: "files.bulk.movePartial",
        failed: "files.bulk.moveFailed"
      })
      clearSelection()
      await reload()
    },
    [reportBulk, clearSelection, reload]
  )

  const bulkDelete = useCallback(async () => {
    const outcomes = await Promise.all(selectedFiles.map(deleteOne))
    reportBulk(outcomes, {
      succeeded: "files.bulk.deleteSucceeded",
      partial: "files.bulk.deletePartial",
      failed: "files.bulk.deleteFailed"
    })
    clearSelection()
    await reload()
  }, [selectedFiles, reportBulk, clearSelection, reload])

  const bulkDownload = useCallback(() => {
    selectedFiles.forEach((file) => void downloadFile(file.fileUrl, file.name))
  }, [selectedFiles])

  const changeStatusOne = (file: FileItem, value: string | null) =>
    file.source === "album-attachment"
      ? changeAttachmentStatus(file.id, value)
      : changeDocumentStatus(file.id, value)

  const changeDocumentTypeOne = (file: FileItem, value: string | null) =>
    file.source === "album-attachment"
      ? renameAttachment(file.id, { document_type: value })
      : updateProjectDocumentClassification(file.id, { document_type: value })

  const bulkChangeStatus = useCallback(
    async (files: FileItem[], value: string | null) => {
      const targets = files.filter((file) => file.canMove)
      if (!targets.length) return
      const outcomes = await Promise.all(
        targets.map((file) => changeStatusOne(file, value))
      )
      reportBulk(outcomes, {
        succeeded: "files.bulk.statusSucceeded",
        partial: "files.bulk.statusPartial",
        failed: "files.bulk.statusFailed"
      })
      clearSelection()
      await reload()
    },
    [reportBulk, clearSelection, reload]
  )

  const bulkChangeDocumentType = useCallback(
    async (files: FileItem[], value: string | null) => {
      const targets = files.filter((file) => file.canMove)
      if (!targets.length) return
      const outcomes = await Promise.all(
        targets.map((file) => changeDocumentTypeOne(file, value))
      )
      reportBulk(outcomes, {
        succeeded: "files.bulk.documentTypeSucceeded",
        partial: "files.bulk.documentTypePartial",
        failed: "files.bulk.documentTypeFailed"
      })
      clearSelection()
      await reload()
    },
    [reportBulk, clearSelection, reload]
  )

  const addToAlbum = useCallback(
    async (albumId: string) => {
      const targetIds = (addingToAlbumFiles ?? [])
        .filter((file) => file.source === "album-attachment")
        .map((file) => file.id)
      if (!targetIds.length) return
      try {
        await dispatch(
          addAlbumAttachments({ id: albumId, data: { attachment_ids: targetIds } })
        ).unwrap()
        message.success(t("files.addToAlbum.succeeded"))
        clearSelection()
      } catch (error) {
        // The album refuses a name it already holds, and only the server can
        // say which file is in the way — the list on screen may be filtered.
        message.error(readServerMessage(error) ?? t("files.addToAlbum.failed"))
      }
    },
    [addingToAlbumFiles, dispatch, message, t, clearSelection]
  )

  const updateDocumentType = useCallback(
    async (file: FileItem, value: string | null): Promise<boolean> => {
      const response = await changeDocumentTypeOne(file, value)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ?? t("files.documentType.failed")
        )
        return false
      }
      await reload()
      return true
    },
    [message, t, reload]
  )

  const updateStatus = useCallback(
    async (file: FileItem, value: string | null): Promise<boolean> => {
      const response = await changeStatusOne(file, value)
      if (!response.ok) {
        // The picker only ever offers legal next statuses, so a rejection
        // here is an edge case (a race, a stale picker) rather than
        // something the user caused — a friendly, generic message, not the
        // backend's transition-graph detail.
        message.error(t("files.status.failed"))
        return false
      }
      await reload()
      return true
    },
    [message, t, reload]
  )

  const submitRequestReview = useCallback(
    async (data: { reviewerId: string; dueDate: string | null; message: string | null }) => {
      if (!requestReviewFile) return
      const response = await requestDocumentReview({
        attachment: requestReviewFile.id,
        reviewer_id: data.reviewerId,
        due_date: data.dueDate,
        message: data.message
      })
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.review.failed"))
        return
      }
      message.success(t("files.review.succeeded"))
      setRequestReviewFile(null)
      await reload()
    },
    [requestReviewFile, message, t, reload]
  )

  const approveReview = useCallback(
    async (review: DocumentReview) => {
      const response = await approveDocumentReview(review.id)
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.review.approveFailed"))
        return
      }
      message.success(t("files.review.approveSucceeded"))
      setReviewRefreshKey((count) => count + 1)
      await reload()
    },
    [message, t, reload]
  )

  const rejectReview = useCallback(
    async (reason: string) => {
      if (!rejectingReview) return
      const response = await rejectDocumentReview(rejectingReview.id, reason)
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.review.rejectFailed"))
        return
      }
      message.success(t("files.review.rejectSucceeded"))
      setRejectingReview(null)
      setReviewRefreshKey((count) => count + 1)
      await reload()
    },
    [rejectingReview, message, t, reload]
  )

  const requestChanges = useCallback(
    async (reason: string) => {
      if (!requestingChangesReview) return
      const response = await requestChangesOnReview(requestingChangesReview.id, reason)
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.review.requestChangesFailed"))
        return
      }
      message.success(t("files.review.requestChangesSucceeded"))
      setRequestingChangesReview(null)
      setReviewRefreshKey((count) => count + 1)
      await reload()
    },
    [requestingChangesReview, message, t, reload]
  )

  const linkRecord = useCallback(
    async (recordType: RelatedRecordType, recordId: string) => {
      if (!linkingFile || linkingFile.source !== "document") return
      const response = await updateProjectDocumentClassification(linkingFile.id, {
        related_record_type: recordType,
        related_record_id: recordId
      })
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ?? t("files.linkRecord.failed")
        )
        return
      }
      message.success(t("files.linkRecord.succeeded"))
      setLinkingFile(null)
      await reload()
    },
    [linkingFile, message, t, reload]
  )

  const unlinkRecord = useCallback(async () => {
    if (!linkingFile || linkingFile.source !== "document") return
    const response = await updateProjectDocumentClassification(linkingFile.id, {
      related_record_type: null,
      related_record_id: null
    })
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.linkRecord.failed"))
      return
    }
    message.success(t("files.linkRecord.unlinked"))
    setLinkingFile(null)
    await reload()
  }, [linkingFile, message, t, reload])

  const restoreVersion = useCallback(
    async (versionId: string) => {
      const response = await restoreProjectDocumentVersion(versionId)
      if (!response.ok || !response.data) {
        message.error(
          readServerMessage(response.data) ?? t("files.versionHistory.restoreFailed")
        )
        return null
      }
      message.success(t("files.versionHistory.restoreSucceeded"))
      setVersionHistoryFile(null)
      await reload()
      return response.data.details.data
    },
    [message, t, reload]
  )

  const fileActions = useCallback(
    (file: FileItem): MenuItemType[] => {
      const items: MenuItemType[] = [
        {
          key: "preview",
          label: t("files.actions.preview"),
          onClick: () => setPreviewFile(file)
        },
        {
          key: "download",
          label: t("files.actions.download"),
          onClick: () => void downloadFile(file.fileUrl, file.name)
        }
      ]
      if (file.canRename) {
        items.push({
          key: "rename",
          label: t("files.actions.rename"),
          onClick: () => setRenamingFile(file)
        })
      }
      if (file.canMove) {
        items.push({
          key: "move",
          label: t("files.actions.move"),
          onClick: () => setMovingFiles([file])
        })
        items.push({
          key: "assignDocumentType",
          label: t("files.actions.assignDocumentType"),
          onClick: () => setAssigningDocumentTypeFile(file)
        })
      }
      if (file.source === "album-attachment" && rights.createAllowed) {
        items.push({
          key: "addToAlbum",
          label: t("files.actions.addToAlbum"),
          onClick: () => setAddingToAlbumFiles([file])
        })
      }
      if (file.source === "document" && file.version !== undefined) {
        if (file.canMove && file.isCurrentVersion !== false) {
          items.push({
            key: "uploadNewVersion",
            label: t("files.uploadNewVersion.action"),
            onClick: () => setUploadingNewVersionFile(file)
          })
        }
        items.push({
          key: "versionHistory",
          label: t("files.versionHistory.action"),
          onClick: () => setVersionHistoryFile(file)
        })
      }
      if (file.source === "document" && file.canShare) {
        items.push({
          key: "share",
          label: t("files.actions.share"),
          onClick: () => setSharingFile(file)
        })
      }
      if (file.source === "document") {
        if (rights.editAllowed && canRequestReview(file.status)) {
          items.push({
            key: "requestReview",
            label: t("files.review.requestAction"),
            onClick: () => setRequestReviewFile(file)
          })
        }
        if (canViewReview(file.status)) {
          items.push({
            key: "viewReview",
            label: t("files.review.viewAction"),
            onClick: () => setReviewingFile(file)
          })
        }
        items.push({
          key: "comments",
          label: t("files.comments.viewAction"),
          onClick: () => setCommentsFile(file)
        })
        items.push({
          key: "activity",
          label: t("files.activity.viewAction"),
          onClick: () => setActivityFile(file)
        })
      }
      if (file.canDelete) {
        items.push({
          key: "delete",
          danger: true,
          label: t("files.actions.moveToTrash"),
          onClick: () => void removeFile(file)
        })
      }
      return items
    },
    [t, rights.createAllowed, rights.editAllowed, removeFile]
  )

  const folderActions = useCallback(
    (folder: FolderListItem): MenuItemType[] => {
      const items: MenuItemType[] = []
      if (canManageFolder(folder)) {
        items.push(
          {
            key: "rename",
            label: t("files.actions.rename"),
            onClick: () => setRenamingFolder(folder)
          },
          {
            key: "move",
            label: t("files.actions.move"),
            onClick: () => void openMoveFolderModal(folder)
          }
        )
      }
      if (rights.deleteAllowed) {
        items.push({
          key: "delete",
          danger: true,
          label: t("files.actions.delete"),
          onClick: () => void removeFolder(folder)
        })
      }
      return items
    },
    [t, canManageFolder, rights.deleteAllowed, openMoveFolderModal, removeFolder]
  )

  const bulkCanDelete =
    selectedFiles.length > 0 && selectedFiles.every((file) => file.canDelete)
  const bulkCanMove =
    selectedFiles.length > 0 && selectedFiles.every((file) => file.canMove)
  const bulkHasAttachments = selectedFiles.some(
    (file) => file.source === "album-attachment"
  )
  // Status/Document Type apply to every file, gated only on whether the
  // caller can edit it — matching the single-row gate in
  // FilesListView/FileDetailsPanel. Never disabled just because some
  // selected files are currently unclassified.
  const bulkCanEditClassification = selectedFiles.some((file) => file.canMove)
  // A status legal for every selected file at once — never the union,
  // which would let one file's illegal move ride along with the rest.
  // Empty (while `bulkCanEditClassification` is true) means the selection's
  // current statuses share no common next step; the bar disables the
  // control rather than silently applying it to only some files.
  const bulkAvailableStatusOptions = (() => {
    const eligible = selectedFiles.filter((file) => file.canMove)
    if (!eligible.length) return []
    const [first, ...rest] = eligible
    const intersection = new Set(first.nextStatuses ?? [])
    for (const file of rest) {
      const allowed = new Set(file.nextStatuses ?? [])
      for (const value of intersection) {
        if (!allowed.has(value)) intersection.delete(value)
      }
    }
    return statusOptions.filter((option) => intersection.has(option.value))
  })()

  return {
    previewFile,
    setPreviewFile,
    newFolderOpen,
    setNewFolderOpen,
    uploadDocumentOpen,
    setUploadDocumentOpen,
    renamingFolder,
    setRenamingFolder,
    movingFiles,
    setMovingFiles,
    movingFolder,
    setMovingFolder,
    allFoldersForMove,
    addingToAlbumFiles,
    setAddingToAlbumFiles,
    renamingFile,
    setRenamingFile,
    assigningDocumentTypeFile,
    setAssigningDocumentTypeFile,
    linkingFile,
    setLinkingFile,
    uploadingNewVersionFile,
    setUploadingNewVersionFile,
    versionHistoryFile,
    setVersionHistoryFile,
    restoreVersion,
    documentTypeOptions,
    documentTypesFailed,
    updateDocumentType,
    statusOptions,
    statusesFailed,
    updateStatus,
    requestReviewFile,
    setRequestReviewFile,
    submitRequestReview,
    reviewingFile,
    setReviewingFile,
    reviewRefreshKey,
    approveReview,
    rejectingReview,
    setRejectingReview,
    rejectReview,
    requestingChangesReview,
    setRequestingChangesReview,
    requestChanges,
    commentsFile,
    setCommentsFile,
    activityFile,
    setActivityFile,
    sharingFile,
    setSharingFile,
    linkRecord,
    unlinkRecord,
    selectedKeys,
    selectedFiles,
    toggleSelection,
    clearSelection,
    canManageFolder,
    createFolderHere,
    renameFolder,
    moveFolderTo,
    moveFile,
    moveFileById,
    renameFile,
    bulkMove,
    bulkDelete,
    bulkDownload,
    bulkChangeStatus,
    bulkChangeDocumentType,
    bulkCanDelete,
    bulkCanMove,
    bulkHasAttachments,
    bulkCanEditClassification,
    bulkAvailableStatusOptions,
    addToAlbum,
    fileActions,
    folderActions,
    restoreFile,
    permanentlyDeleteFile,
    emptyTrash
  }
}
